<template>
  <div class="leaderboard-widget">
    <!-- Collapsed: Single line -->
    <div v-if="!expanded" class="d-flex align-items-center justify-content-between p-2 border rounded" :class="compact ? 'bg-transparent border-0' : 'bg-light'">
      <div class="d-flex align-items-center">
        <i class="bi bi-trophy-fill text-warning me-2"></i>
        <span class="fw-semibold" :class="compact ? 'small' : ''">{{ title }}</span>
      </div>
      <button
        @click="expanded = true"
        class="btn btn-sm btn-link text-muted p-0 ms-2"
        style="min-width: auto;"
        :title="`Expand ${title}`"
      >
        <i class="bi bi-chevron-down" style="font-size: 1.1rem;"></i>
      </button>
    </div>

    <!-- Expanded: Full view -->
    <div v-else>
      <div class="d-flex align-items-center justify-content-between mb-3">
        <h5 class="mb-0" :class="compact ? 'h6' : ''">
          <i class="bi bi-trophy-fill text-warning"></i>
          {{ title }}
        </h5>
        <button
          @click="expanded = false"
          class="btn btn-sm btn-link text-muted p-0 ms-2"
          style="min-width: auto;"
          :title="`Collapse ${title}`"
        >
          <i class="bi bi-chevron-up" style="font-size: 1.1rem;"></i>
        </button>
      </div>

    <!-- Loading state -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-if="error" class="alert alert-danger" role="alert">
      Failed to load leaderboard. {{ error }}
    </div>

    <!-- Leaderboard content -->
    <div v-if="!loading && !error">
      <!-- Current user's stats card -->
      <div v-if="myStats" class="card mb-3 border-primary">
        <div class="card-body">
          <h6 class="card-title">Your Stats</h6>
          <div class="row g-2 small">
            <div class="col-6">
              <div class="text-muted">Points</div>
              <div class="h5 mb-0">{{ myStats.engagement_points }}</div>
            </div>
            <div class="col-6">
              <div class="text-muted">Rank</div>
              <div class="h5 mb-0">#{{ userRank }} / {{ totalUsers }}</div>
            </div>
            <div class="col-6">
              <div class="text-muted">Page Views</div>
              <div>{{ myStats.total_page_views }}</div>
            </div>
            <div class="col-6">
              <div class="text-muted">Feedback Given</div>
              <div>{{ myStats.total_feedback_given }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Top users list -->
      <div class="list-group">
        <div
          v-for="(user, index) in displayedLeaderboard"
          :key="user.user_id"
          class="list-group-item"
          :class="{ 'list-group-item-primary': user.user_id === currentUserId }"
        >
          <div class="d-flex align-items-center">
            <!-- Rank badge -->
            <div class="me-3">
              <span
                class="badge rounded-circle"
                :class="getRankBadgeClass(index)"
                style="width: 2.5rem; height: 2.5rem; line-height: 2.5rem; font-size: 1rem;"
              >
                {{ index + 1 }}
              </span>
            </div>

            <!-- User info -->
            <div class="flex-grow-1">
              <div class="fw-semibold">
                {{ user.user_name }}
                <span v-if="user.user_id === currentUserId" class="badge bg-primary ms-2">You</span>
              </div>
              <div class="small text-muted">
                {{ user.engagement_points }} points
                • {{ user.total_page_views }} views
                • {{ user.total_feedback_given }} feedback
              </div>
            </div>

            <!-- Trophy for top 3 -->
            <div v-if="index < 3" class="ms-2">
              <i
                class="bi bi-trophy-fill"
                :class="getTrophyClass(index)"
                style="font-size: 1.5rem;"
              ></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Refresh button -->
      <div class="text-center mt-3">
        <button
          @click="loadLeaderboard"
          class="btn btn-sm btn-outline-secondary"
          :disabled="loading"
        >
          <i class="bi bi-arrow-clockwise"></i>
          Refresh
        </button>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import http from '@/api/http'

export default {
  name: 'LeaderboardWidget',
  props: {
    autoRefresh: {
      type: Boolean,
      default: false
    },
    refreshInterval: {
      type: Number,
      default: 300000 // 5 minutes
    },
    compact: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Engagement Leaderboard'
    }
  },
  data() {
    return {
      leaderboard: [],
      myStats: null,
      userRank: null,
      totalUsers: 0,
      currentUserId: null,
      loading: false,
      error: null,
      expanded: false, // Start collapsed
      displayLimit: 0, // Show none initially when collapsed
      refreshTimer: null
    }
  },
  computed: {
    displayedLeaderboard() {
      if (this.expanded) {
        return this.leaderboard
      }
      return this.leaderboard.slice(0, this.displayLimit)
    }
  },
  mounted() {
    this.loadLeaderboard()
    this.loadMyStats()

    // Get current user ID from localStorage or API
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    this.currentUserId = user.id

    // Set up auto-refresh if enabled
    if (this.autoRefresh) {
      this.refreshTimer = setInterval(() => {
        this.loadLeaderboard()
        this.loadMyStats()
      }, this.refreshInterval)
    }
  },
  beforeUnmount() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
  },
  methods: {
    async loadLeaderboard() {
      this.loading = true
      this.error = null

      try {
        const response = await http.get('/api/v1/feedback/leaderboard', {
          params: { limit: this.expanded ? 50 : 20 }
        })

        this.leaderboard = response.data.leaderboard || []
        this.userRank = response.data.user_rank
        this.totalUsers = response.data.total_users
      } catch (error) {
        console.error('Failed to load leaderboard:', error)
        this.error = error.response?.data?.message || 'Unknown error'
      } finally {
        this.loading = false
      }
    },

    async loadMyStats() {
      try {
        const response = await http.get('/api/v1/feedback/stats/me')
        this.myStats = response.data
      } catch (error) {
        console.error('Failed to load user stats:', error)
        // Don't show error - just hide the stats card
      }
    },

    getRankBadgeClass(index) {
      if (index === 0) return 'bg-warning text-dark' // Gold
      if (index === 1) return 'bg-secondary' // Silver
      if (index === 2) return 'bg-danger' // Bronze
      return 'bg-light text-dark'
    },

    getTrophyClass(index) {
      if (index === 0) return 'text-warning' // Gold
      if (index === 1) return 'text-secondary' // Silver
      if (index === 2) return 'text-danger' // Bronze
      return ''
    }
  }
}
</script>

<style scoped>
.leaderboard-widget {
  max-width: 600px;
}

.list-group-item {
  transition: background-color 0.2s;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.list-group-item-primary {
  background-color: #cfe2ff;
  border-color: #b6d4fe;
}

.badge.rounded-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
